// packages
import React, { useEffect } from "react";
import { PaymentElement } from "@stripe/react-stripe-js";

// contexts
import { useAppLayerContext } from "@/shared/contexts/AppLayer";

// components
import { Alert, AlertTitle, AlertDescription } from "@/shared/components/ui/alert";
import { FeatureSlot } from "@/shared/components/FeatureSlot";
import { FeatureSlotIds } from "@/shared/components/FeatureSlot/slotIds";
import { UsQuoteFormSteps } from "@/quote-ptz-us/formConfigTypes";

// utils
import StripeUtils from "./StripeUtils";
import { UIUtils } from "@/shared/utils/UIUtils";
import Strings from "@/shared/utils/Strings.constants";
import { AnalyticsUtils } from "@/shared/utils/AnalyticsUtils";

// types
import { Quote } from "@/shared/types/Quote.interface";

// media
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FieldsOption, StripePaymentElementOptions } from "@stripe/stripe-js";
import { cn } from "@/shared/utils";

interface StripePaymentElementProps {
    fields?: FieldsOption;
    paymentOptions?: StripePaymentElementOptions;
    quote?: Quote;
    className?: string;
}

export default function StripePaymentElement(props: StripePaymentElementProps) {
    const { paymentOptions, quote, className } = props;
    const { appState } = useAppLayerContext();
    const { stripePaymentElementError, stripeElements } = appState;
    const phoneHours = `${UIUtils.formatPhone(Strings.PTZ_US.PHONE_NUMBER)}. ${UIUtils.getPhoneHours("PTZ_US")}`;

    useEffect(() => {
        if (!!stripePaymentElementError?.stripe && !!stripeElements) {
            AnalyticsUtils.sendDataDogLog("error", "PAYMENT - server validation error", {
                stripeError: stripePaymentElementError.message ?? stripePaymentElementError.header ?? "Unknown error"
            });
            const paymentElement = stripeElements.getElement("payment");
            paymentElement?.focus();
        }
    }, [stripePaymentElementError, stripeElements]);

    const paymentElementOptions: StripePaymentElementOptions = { ...StripeUtils.paymentElementOptions, ...paymentOptions };

    return (
        <>
            <FeatureSlot stepId={UsQuoteFormSteps.BILLING.id} slotId={FeatureSlotIds.PAYMENT} formId={appState.formID} quote={quote} />

            <PaymentElement id="stripe-payment-element" className={cn("mt-5", className)} options={paymentElementOptions} />
            {!!stripePaymentElementError?.stripe && (
                <Alert variant="destructive" className="mt-5">
                    <FontAwesomeIcon icon={faInfoCircle} size="lg" />
                    <AlertTitle className="text-content-danger">{stripePaymentElementError?.header ?? Strings.ERRORS.DECLINE_GENERIC}</AlertTitle>
                    <AlertDescription>
                        {stripePaymentElementError?.description ?? `Double check your payment details and try again, or give us a call: ${phoneHours}`}
                    </AlertDescription>
                </Alert>
            )}
        </>
    );
}
